/**
 * Centered overlay for confirmations and short forms. Destructive
 * confirmations use the error icon treatment.
 * Spec: assets/handoffs/design_system_export/specs/Modal.json
 *
 * @param {boolean} open
 * @param {function} onClose
 * @param {'default'|'lg'|'settings'=default} size - 400px / 544px / 720px panel width
 * @param {'neutral'|'warning'|'destructive'=neutral} variant - only affects the featuredIcon treatment and whether overlay-click closes ('neutral' only)
 * @param {ReactNode=} icon - featured icon glyph; omit to skip the featured-icon slot
 * @param {ReactNode=} title
 * @param {ReactNode=} children - body content
 * @param {ReactNode=} footer - action buttons; cancel first, confirm last
 */
import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import IconButton from "../IconButton/IconButton";
import Icon from "../icons/Icon";
import styles from "./Modal.module.scss";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const Modal = ({
  open,
  onClose,
  size = "default",
  variant = "neutral",
  icon,
  title,
  children,
  footer,
  className,
  ...rest
}) => {
  const panelRef = useRef(null);
  const previousActiveElement = useRef(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    if (!open) return undefined;

    previousActiveElement.current = document.activeElement;
    // Skip if an autoFocus descendant (e.g. an Input) already grabbed focus
    // during this same commit — don't steal it back to the panel.
    if (!panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus();
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
        return;
      }
      if (e.key !== "Tab") return;

      const focusable = panelRef.current?.querySelectorAll(FOCUSABLE_SELECTOR);
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
      previousActiveElement.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className={styles.overlay} onClick={variant === "neutral" ? onClose : undefined}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={children ? bodyId : undefined}
        tabIndex={-1}
        className={classNames(styles.panel, styles[`panel--${size}`], className)}
        onClick={(e) => e.stopPropagation()}
        {...rest}
      >
        <IconButton
          size="sm"
          variant="ghost"
          icon={<Icon name="XClose" />}
          label="Close"
          className={styles.closeButton}
          onClick={onClose}
        />
        {icon && (
          <span className={classNames(styles.featuredIcon, styles[`featuredIcon--${variant}`])}>
            {icon}
          </span>
        )}
        {title && (
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
        )}
        {children && (
          <div id={bodyId} className={styles.body}>
            {children}
          </div>
        )}
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
